import {
  industriesSubIndustriesCreateMutation,
  industriesSubIndustriesDestroyMutation,
  industriesSubIndustriesListOptions,
  industriesSubIndustriesListQueryKey,
  industriesSubIndustriesPartialUpdateMutation,
  industriesSubIndustriesRetrieveOptions,
  ReturnSubIndustryReadable
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import { FormNomenclaturesSubIndustries } from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import {
  DayLabel,
  TableActions,
  TableContainer,
  TitleSection
} from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form, MenuProps, Modal, TableColumnsType } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

export const NomenclaturesSubIndustries = () => {
  const { id } = useParams()
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { resetPagination } = useResetPagination()
  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const [form] = Form.useForm()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)

  const { data: subIndustries, isLoading } = useQuery({
    ...industriesSubIndustriesListOptions({
      path: { industry_id: String(id) },
      query: { ...defaultFilters, ...params }
    }),
    enabled: !!id
  })

  const { data: subIndustry, isLoading: isLoadingSubIndustry } = useQuery({
    ...industriesSubIndustriesRetrieveOptions({
      path: { id: Number(editItem), industry_id: String(id) }
    }),
    enabled: !!editItem
  })

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<ReturnSubIndustryReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
  }

  const deleteSubIndustryMutation = useMutation({
    ...industriesSubIndustriesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      resetPagination()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const createSubIndustryMutation = useMutation({
    ...industriesSubIndustriesCreateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const updateSubIndustryMutation = useMutation({
    ...industriesSubIndustriesPartialUpdateMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      onSuccessMutation()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  })

  const onSuccessMutation = () => {
    invalidateQueries([
      industriesSubIndustriesListQueryKey({
        path: { industry_id: String(id) }
      })
    ])
    onCloseModal()
  }

  const onDelete = () =>
    deleteItem &&
    deleteSubIndustryMutation.mutate({
      path: { id: deleteItem, industry_id: String(id) }
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    return editItem
      ? updateSubIndustryMutation.mutate({
          path: { id: editItem, industry_id: String(id) },
          body: values
        })
      : createSubIndustryMutation.mutate({
          body: values,
          path: { industry_id: String(id) }
        })
  }

  useEffect(() => {
    if (subIndustry) {
      form.setFieldsValue(subIndustry)
    }
  }, [subIndustry])

  return (
    <>
      <TitleSection title={t(`nomenclature.sub_industries`)} />

      <TableContainer
        data={subIndustries}
        columns={columns}
        loading={isLoading}
        onChange={(_, __, result) => sorter(result)}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_sub_industry')}
          </Button>
        }
        bordered
      />

      <Modal
        open={!!(editItem ?? isCreate)}
        onCancel={onCloseModal}
        title={t(!id ? 'nomenclature.add_entity' : 'nomenclature.edit_entity')}
        loading={isLoadingSubIndustry}
        okButtonProps={{
          htmlType: 'submit',
          loading:
            updateSubIndustryMutation.isPending ||
            createSubIndustryMutation.isPending,
          onClick: onSubmit
        }}
      >
        <Form form={form} layout="vertical">
          <FormNomenclaturesSubIndustries />
        </Form>
      </Modal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteSubIndustryMutation.isPending}
      />
    </>
  )
}
